import { defineStore } from 'pinia';
import useCommonStore from '@/common/store/pinia/common';
import { ComputeImage as ComputeImageSchema } from '@schema';
import mapper, { ComputeImage } from '@/common/store/pinia/computeImages/computeImageMapper';
import { computed, del, shallowRef } from 'vue';
import encodeBase64 from '@/common/utils/base64';
import { store } from '@/common/root';
import { uploadProgressFormatter } from '@/common/utils/formatter.image';
import { NotificationType } from '@/common/constants/common.constants';
import i18n from '@/common/i18n';
import api from '@/common/utils/api';
import { throttle } from 'lodash-es';
import { computeNotificationsHandler, OperationActions } from '@/common/utils/computeNotificationsHandler';

type ImageField = Pick<ComputeImageSchema, 'name' | 'container_format' | 'disk_format' | 'os_distro' | 'protected' | 'visibility' | 'hw_firmware_type' | 'hw_tpm_version'>;

function createNotificationPayload(id: ID, fileName: string, fileSize: number) {
  return {
    type: NotificationType.PROGRESS,
    id,
    progress: 0,
    name: fileName,
    title: i18n.t('Uploading image "{name}"', { name: fileName }),
    message: uploadProgressFormatter(0, fileSize),
    showClose: true,
    preventReload: true,
    skipStore: true,
    actions: [
      {
        label: i18n.t('Cancel'),
        name: 'abortImage',
        payload: {
          id,
          fileName
        }
      }
    ]
  };
}

export const useComputeImagesStore = defineStore('computeImages', () => {
  const commonStore = useCommonStore<ComputeImageSchema, ComputeImage>({
    getUrl: () => '/api/v2/compute/images',
    mapItem: mapper,
    pagination: 'common'
  })();

  const { getItemById, fetchItem } = commonStore;

  const uploadInstances = shallowRef<Record<ID, XMLHttpRequest>>({});

  const getDiskFormat = computed(() => (id: ComputeImage['id']) => getItemById.value(id)?.format);

  const onProgress = (temporaryId: ID, progress: ProgressEvent, xhr: XMLHttpRequest) => {
    if (!uploadInstances.value[temporaryId]) {
      uploadInstances.value[temporaryId] = xhr;
    }

    store.dispatch('notifications/UPDATE', {
      id: temporaryId,
      progress: progress.loaded / progress.total * 100,
      message: uploadProgressFormatter(progress.loaded, progress.total),
      preventReload: progress.loaded < progress.total,
      showClose: true
    });
  };

  const upload = async (temporaryId: ID, fields: ImageField, body: File) => {
    const options = {
      method: 'post',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/octet-stream',
        'x-hci-image-name': encodeBase64(fields.name),
        'x-hci-image-container-format': fields.container_format,
        'x-hci-Image-disk-format': fields.disk_format,
        'x-hci-image-os-distro': fields.os_distro,
        'x-hci-image-protected': fields.protected,
        'x-hci-image-visibility': fields.visibility,
      },
      uploadTimeout: 60000
    };

    if (fields.hw_tpm_version) {
      options.headers['x-hci-image-hw-tpm-version'] = fields.hw_tpm_version;
    }

    if (fields.disk_format !== 'iso' && fields.hw_firmware_type) {
      options.headers['x-hci-image-hw-firmware-type'] = fields.hw_firmware_type;
    }

    await store.dispatch('notifications/ADD', createNotificationPayload(temporaryId, fields.name, body.size));
    return api.upload('/api/v2/compute/images', body, options, (process, xhr) => onProgress(temporaryId, process, xhr))
      .finally(() => {
        del(uploadInstances.value, temporaryId);
      });
  };

  const abort = async (temporaryId: ID) => {
    await store.dispatch('notifications/REMOVE', temporaryId);
    if (uploadInstances.value[temporaryId]) {
      uploadInstances.value[temporaryId].abort();
      del(uploadInstances.value, temporaryId);
    }
  };

  const onCompute = async (event: SocketEvent): Promise<void> => {
    if (!event) {
      return;
    }

    const { object } = event;
    const throttledFetchItem = throttle(() => fetchItem(object.id as ID), 300);

    const operationActions: OperationActions = {
      created: { fn: throttledFetchItem },
      updated: { fn: throttledFetchItem },
    };

    await computeNotificationsHandler({
      eventType: 'images',
      resource: 'compute_image',
      operationActions
    })(event);
  };

  return {
    ...commonStore,
    uploadInstances,
    getDiskFormat,
    onProgress,
    upload,
    abort,
    onCompute
  };
}, {
  ws: {
    compute: 'onCompute'
  }
});

export default useComputeImagesStore;
